import React, { useRef, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { API_BASE_URL } from "../api";

/**
 * Represents an item in the shopping cart
 */
interface CartItem {
  /** Name of the item */
  name: string;
  /** Price of the item in currency units */
  price: number;
  /** Quantity of the item in cart */
  quantity: number;
}

/**
 * Props for the Cart component
 */
interface CartProps {
  /** Controls whether the cart modal is visible */
  isOpen: boolean;
  /** Callback function to close the cart modal */
  onClose: () => void;
  /** Array of items currently in the cart */
  items: CartItem[];
  /** Optional callback to remove an item from the cart */
  onRemoveItem?: (index: number) => void;
  /** Optional callback to update the quantity of an item */
  onUpdateQuantity?: (index: number, quantity: number) => void;
}

const quantityButtonClass =
  "flex size-11 items-center justify-center rounded-full bg-gray-200 text-lg font-bold text-black hover:bg-gray-300";

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as
  | string
  | undefined;
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

/**
 * Cart component that displays a modal with the current shopping cart contents.
 */
const Cart: React.FC<CartProps> = ({
  isOpen,
  onClose,
  items,
  onRemoveItem,
  onUpdateQuantity,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setCheckoutError(null);
      setIsCheckingOut(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const total = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const handleCheckout = async () => {
    if (items.length === 0 || isCheckingOut) return;

    setIsCheckingOut(true);
    setCheckoutError(null);

    try {
      if (!stripePromise || !stripePublishableKey) {
        setCheckoutError(
          "Stripe test checkout is not configured yet. Add VITE_STRIPE_PUBLISHABLE_KEY (pk_test_…) and STRIPE_SECRET_KEY (sk_test_…) to your env files. Cart changes still work locally.",
        );
        return;
      }

      if (!stripePublishableKey.startsWith("pk_test_")) {
        setCheckoutError(
          "Live Stripe keys are blocked for now. Use a pk_test_ publishable key so nothing is charged for real.",
        );
        return;
      }

      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((item) => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
          })),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setCheckoutError(
          typeof data.detail === "string"
            ? data.detail
            : "Could not start checkout. Check that Stripe test keys are set on the server.",
        );
        return;
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      const stripe = await stripePromise;
      if (!stripe || !data.sessionId) {
        setCheckoutError("Stripe failed to initialize checkout.");
        return;
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });
      if (error) {
        setCheckoutError(error.message ?? "Checkout redirect failed.");
      }
    } catch {
      setCheckoutError(
        "Checkout is unavailable right now. Your cart is still saved locally.",
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div
        ref={modalRef}
        className="relative flex max-h-[90vh] w-full max-w-md flex-col overflow-y-auto rounded-lg bg-white p-4 text-black shadow-xl sm:p-6"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex size-11 items-center justify-center rounded-lg text-2xl hover:bg-gray-100"
          aria-label="Close cart"
        >
          ×
        </button>

        <h2 className="mb-4 pr-10 text-2xl font-bold">Your Cart</h2>

        {items.length === 0 ? (
          <p className="text-center">Your cart is empty</p>
        ) : (
          <>
            <div className="mb-4 min-h-0 flex-1 overflow-y-auto">
              {items.map((item, index) => (
                <div
                  key={`${item.name}-${index}`}
                  className="mb-2 flex flex-col gap-3 rounded-lg bg-gray-100 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <h3 className="font-semibold">{item.name}</h3>
                    <p>
                      ${item.price.toFixed(2)} × {item.quantity}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateQuantity?.(index, item.quantity - 1)
                        }
                        className={quantityButtonClass}
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateQuantity?.(index, item.quantity + 1)
                        }
                        className={quantityButtonClass}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    {onRemoveItem && (
                      <button
                        type="button"
                        onClick={() => onRemoveItem(index)}
                        className="min-h-[44px] px-2 text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="shrink-0 border-t pt-4">
              <div className="mb-4 flex justify-between text-xl font-bold">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
              {checkoutError && (
                <p className="mb-3 text-sm text-amber-800" role="alert">
                  {checkoutError}
                </p>
              )}
              <p className="mb-3 text-xs text-gray-500">
                Checkout uses Stripe test mode only — no real charges until live
                keys are enabled later.
              </p>
              <button
                type="button"
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className="min-h-[44px] w-full rounded-lg bg-blue-500 px-6 py-3 text-lg font-bold text-white transition-all hover:bg-blue-600 active:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCheckingOut ? "Starting checkout…" : "Proceed to Checkout"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
